import * as React from "react";
import { CalendarDays } from "lucide-react";
import { el } from "date-fns/locale";
import { cn } from "@/lib/utils";
import { Input } from "@/components/ui/input";
import { Calendar } from "@/components/ui/calendar";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";

const ISO_RE = /^\d{4}-\d{2}-\d{2}$/;

const isoToText = (iso) => (ISO_RE.test(iso || "") ? `${iso.slice(8, 10)}/${iso.slice(5, 7)}/${iso.slice(0, 4)}` : "");
const isoToDate = (iso) => (ISO_RE.test(iso || "") ? new Date(+iso.slice(0, 4), +iso.slice(5, 7) - 1, +iso.slice(8, 10)) : undefined);
const dateToIso = (d) => `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}-${String(d.getDate()).padStart(2, "0")}`;
const maskDigits = (digits) => {
  const d = digits.slice(0, 8);
  if (d.length <= 2) return d;
  if (d.length <= 4) return `${d.slice(0, 2)}/${d.slice(2)}`;
  return `${d.slice(0, 2)}/${d.slice(2, 4)}/${d.slice(4)}`;
};
const digitsToIso = (digits) => {
  if (digits.length !== 8) return null;
  const dd = +digits.slice(0, 2), mm = +digits.slice(2, 4), yyyy = +digits.slice(4);
  const dt = new Date(yyyy, mm - 1, dd);
  if (yyyy < 1900 || dt.getFullYear() !== yyyy || dt.getMonth() !== mm - 1 || dt.getDate() !== dd) return null;
  return dateToIso(dt);
};

/** Ημερομηνία ΗΗ/ΜΜ/ΕΕΕΕ (πληκτρολόγηση με μάσκα + ελληνικό ημερολόγιο). Τιμή/onChange σε ISO (YYYY-MM-DD) — drop-in για <Input type="date">. */
const splitClasses = (className) => {
  const tokens = (className || "").split(/\s+/).filter(Boolean);
  const base = (c) => c.replace(/^(sm|md|lg|xl|2xl):/, "");
  return {
    wrapper: tokens.filter((c) => /^(w-|min-w|max-w|m[trblxy]?-|flex|col-span|block|inline|grow|shrink|basis|self-)/.test(base(c))).join(" "),
    input: tokens.filter((c) => /^(h-|text-|font-)/.test(base(c))).join(" "),
  };
};

const DateInput = React.forwardRef(({ value, onChange, className, min, max, disabled, required, name, id, placeholder = "ΗΗ/ΜΜ/ΕΕΕΕ", ...props }, ref) => {
  const cls = splitClasses(className);
  const [text, setText] = React.useState(() => isoToText(value));
  const [open, setOpen] = React.useState(false);
  const testId = props["data-testid"];
  React.useEffect(() => { setText(isoToText(value)); }, [value]);

  const emit = (iso) => {
    if (iso && ((min && iso < min) || (max && iso > max))) return false;
    if (iso !== (value || "")) onChange?.({ target: { value: iso, name } });
    return true;
  };
  const handleText = (e) => {
    const raw = e.target.value;
    if (ISO_RE.test(raw)) { setText(isoToText(raw)); emit(raw); return; }
    const digits = raw.replace(/\D/g, "");
    setText(maskDigits(digits));
    if (digits.length === 0) emit("");
    else if (digits.length === 8) { const iso = digitsToIso(digits); if (iso) emit(iso); }
  };
  const handleBlur = () => {
    const iso = digitsToIso(text.replace(/\D/g, ""));
    const valid = iso && !(min && iso < min) && !(max && iso > max);
    if (text !== "" && (!valid || iso !== (value || ""))) setText(isoToText(value));
  };

  return (
    <div className={cn("relative flex items-center", cls.wrapper)} data-testid={testId ? `${testId}-wrapper` : undefined}>
      <Input ref={ref} type="text" inputMode="numeric" autoComplete="off" value={text} onChange={handleText} onBlur={handleBlur}
             placeholder={placeholder} disabled={disabled} required={required} name={name} id={id} maxLength={10}
             className={cn("pr-8 w-full", cls.input)} {...props} />
      <Popover open={open} onOpenChange={setOpen}>
        <PopoverTrigger asChild>
          <button type="button" tabIndex={-1} disabled={disabled} aria-label="Άνοιγμα ημερολογίου"
                  className="absolute right-1.5 top-1/2 -translate-y-1/2 rounded p-0.5 text-slate-400 hover:text-slate-700 disabled:opacity-40"
                  data-testid={testId ? `${testId}-calendar` : undefined}>
            <CalendarDays className="h-4 w-4" />
          </button>
        </PopoverTrigger>
        <PopoverContent className="w-auto p-0" align="start">
          <Calendar mode="single" locale={el} selected={isoToDate(value)} defaultMonth={isoToDate(value) || new Date()} weekStartsOn={1}
                    fromDate={isoToDate(min)} toDate={isoToDate(max)}
                    onSelect={(d) => { if (d) { emit(dateToIso(d)); } setOpen(false); }} />
        </PopoverContent>
      </Popover>
    </div>
  );
});
DateInput.displayName = "DateInput";

export { DateInput };
